import { AxiosRequestConfig } from "axios";
import { callExternalApi, ApiResponse } from "./external-api.service";
import { getLogger } from "src/utils/logger";

const logger = getLogger('correspondence.service');
const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

export interface CorrespondenceRequest {
  type: 'newsletter' | 'email' | 'blogpost';
  data: any;
}

export interface CorrespondenceResponse {
  content: string;
  documentId?: string;
}

/**
 * Generate correspondence content based on the provided form data
 *
 * @param correspondenceData The correspondence request containing type and form data
 * @param accessToken The user's access token for authentication
 * @returns A promise resolving to an ApiResponse containing the generated content or an error
 */
export const generateCorrespondence = async (
  correspondenceData: CorrespondenceRequest,
  accessToken: string
): Promise<ApiResponse<CorrespondenceResponse>> => {
  const config: AxiosRequestConfig = {
    url: `${apiServerUrl}/api/documents/correspondence`,
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    data: correspondenceData,
  };
  
  logger.debug("Generating correspondence content", { type: correspondenceData.type });
  const response = await callExternalApi({ config });
  logger.debug(`Response received with status code: ${response.data?.status || 'unknown'}`);
  
  if (response.error || !response.data) {
    return {
      data: null,
      error: response.error || { message: "Failed to generate correspondence content" }
    };
  }
  
  try {
    // Extract content from the backend response
    const { content, document_id } = response.data;
    
    // Parse content if it's a string, otherwise use as is
    const correspondenceContent: string = typeof content === 'string' 
      ? content 
      : JSON.stringify(content);
    
    logger.debug(`Correspondence content generated successfully`);
    
    return {
      data: {
        content: correspondenceContent,
        documentId: document_id
      },
      error: null
    };
  } catch (parseError) {
    logger.error("Failed to parse content:", parseError);
    return {
      data: null,
      error: { message: "Invalid response format from API" }
    };
  }
}; 